import { Region } from '../types';

export const homepageTranslations: Record<Region, {
  hero: {
    /** Small line above the H1 (rendered as <p>, not a heading). */
    eyebrow?: string;
    headline: string;
    headlineHighlight?: string;
    headlineSuffix?: string;
    /** Label of the booking pill inside the outcome card of the hero funnel. */
    cta: string;
    /** Text in front of the client logos. */
    proofText?: string;
  };
  problem: {
    headline: string;
    headlineHighlight: string;
    subtitle: string;
    problems: {
      title: string;
      description: string;
    }[];
    conclusion: string;
    conclusionHighlight: string;
  };
  positioning: {
    headline: string;
    headlineHighlight: string;
    headlineSuffix?: string;
    subtitle: string;
    connectsLabel: string;
    pillars: string[];
    conclusionPrefix: string;
    conclusionHighlight: string;
  };
  services: {
    headline: string;
    headlineHighlight: string;
    subtitle: string;
    topRow: { title: string; description: string }[];
    middleRow: { title: string; description: string }[];
    bottomBlock: { title: string; description: string };
  };
  faq: {
    headline: string;
    headlineHighlight: string;
    questions: { question: string; answer: string }[];
  };
  finalCta: {
    badge: string;
    headline: string;
    headlineHighlight: string;
    subtitle: string;
    cta: string;
    disclaimer: string;
  };
}> = {
  de: {
    hero: {
      eyebrow: 'LinkedIn Demand Generation für B2B',
      headline: 'Planbare Pipeline für erklärungsbedürftige B2B-Angebote.',
      cta: 'Strategiegespräch buchen',
      proofText: 'Erprobt bei 250+ B2B-Unternehmen',
    },
    problem: {
      headline: 'Warum LinkedIn oft',
      headlineHighlight: 'keine planbare Pipeline erzeugt.',
      subtitle: 'LinkedIn funktioniert. Aber isolierte Maßnahmen führen selten zu wiederholbaren Ergebnissen. Vier typische Gründe:',
      problems: [
        { title: 'Company Pages skalieren nicht', description: 'Ihre Unternehmensseite erzeugt Reichweite, aber keine neue Nachfrage. Algorithmus und Format begrenzen die Wirkung.' },
        { title: 'Corporate Influencer ohne System', description: 'Ihre Führungskräfte werden sichtbar, aber ohne Struktur entstehen Impressionen, keine qualifizierten Gespräche.' },
        { title: 'LinkedIn Ads allein sind teuer', description: 'Ads erzeugen Klicks, aber ohne Outbound und Nachfassung bleibt die Conversion niedrig, der CPL hoch.' },
        { title: 'Keine saubere Übergabe an Sales', description: 'Aktivitäten laufen parallel, aber Ihr Sales-Team erhält keine priorisierten, qualifizierten Kontakte mit Kontext.' },
      ],
      conclusion: 'LinkedIn scheitert nicht am Kanal,',
      conclusionHighlight: 'sondern an der fehlenden Architektur dahinter.',
    },
    positioning: {
      headline: 'Prometheus denkt LinkedIn',
      headlineHighlight: 'ganzheitlich',
      headlineSuffix: 'von Sichtbarkeit bis zum Termin.',
      subtitle: 'Sie sind bereits auf LinkedIn aktiv und merken, dass dort noch deutlich mehr möglich ist. Genau da setzen wir an.',
      connectsLabel: 'Prometheus verbindet:',
      pillars: ['LinkedIn Ads', 'LinkedIn Outbound', 'Corporate Influencer', 'Tracking & Attribution', 'Qualifizierung & Terminierung'],
      conclusionPrefix: 'zu einem durchgängigen',
      conclusionHighlight: 'B2B-Revenue-Setup.',
    },
    services: {
      headline: 'Unsere',
      headlineHighlight: 'Leistungen',
      subtitle: 'Ein durchgängiger Funnel für planbare B2B-Gespräche über LinkedIn.',
      topRow: [
        { title: 'LinkedIn Ads & Demand Generation', description: 'Kampagnen für Ihre erklärungsbedürftigen B2B-Angebote' },
        { title: 'LinkedIn Outbound & Social Selling', description: 'Strukturierte Outbound-Strategien auf Entscheider-Ebene' },
        { title: 'Corporate Influencer & Sichtbarkeit', description: 'Aufbau und Skalierung Ihrer Personenmarken' },
      ],
      middleRow: [
        { title: 'Tracking, Daten & Attribution', description: 'Sauberes Tracking aller LinkedIn-Aktivitäten auf Lead-, Kontakt- und Account-Ebene' },
        { title: 'Pipeline- & Sales-Unterstützung', description: 'Aktives Nachfassen Ihrer Leads & Opportunities für mehr Abschlüsse' },
      ],
      bottomBlock: { title: 'Qualifizierte Termine', description: 'LinkedIn erzeugt Interesse. Prometheus verwandelt es in planbare B2B-Gespräche für Ihr Team.' },
    },
    faq: {
      headline: 'Häufig gestellte',
      headlineHighlight: 'Fragen',
      questions: [
        { question: 'Für welche Unternehmen ist Prometheus geeignet?', answer: 'Prometheus arbeitet ausschließlich mit B2B-Unternehmen, die erklärungsbedürftige Angebote mit längeren Sales-Zyklen verkaufen. Typische Kunden sind IT-Dienstleister, Beratungen, SaaS-Unternehmen und spezialisierte B2B-Anbieter im DACH-Raum und Europa.' },
        { question: 'Wie funktioniert die Termin-Garantie?', answer: 'Sie definieren gemeinsam mit uns die Zielgruppe und die gewünschte Anzahl an Terminen. Unser Telemarketing-Team qualifiziert Leads und vereinbart Gespräche. Wir liefern so lange, bis die vereinbarte Anzahl an stattgefundenen Terminen erreicht ist.' },
        { question: 'Brauchen wir bereits einen LinkedIn-Auftritt?', answer: 'Nein, nicht zwingend. Prometheus arbeitet sowohl mit Unternehmen, die bereits auf LinkedIn aktiv sind, als auch mit solchen, die ganz am Anfang stehen. Gemeinsam analysieren wir, wo der größte Hebel liegt.' },
        { question: 'Wie lange dauert es, bis erste Ergebnisse sichtbar werden?', answer: 'Die ersten qualifizierten Termine entstehen typischerweise innerhalb von 4 bis 8 Wochen nach Projektstart. Die genaue Timeline hängt von der Komplexität Ihres Angebots und Ihrer Zielgruppe ab.' },
        { question: 'Wie funktioniert die Integration mit unserem CRM?', answer: 'Alle Aktivitäten werden nahtlos in gängige CRM-Systeme wie HubSpot, Salesforce oder Microsoft Dynamics integriert. So haben Sie volle Transparenz über alle Touchpoints und können die Pipeline-Wirkung direkt nachverfolgen.' },
        { question: 'Was unterscheidet Prometheus von anderen LinkedIn-Agenturen?', answer: 'Prometheus denkt LinkedIn ganzheitlich: von der ersten Sichtbarkeit bis zum qualifizierten Termin. Im Gegensatz zu reinen Ads- oder Outbound-Agenturen verbinden wir alle Disziplinen und garantieren echte Gespräche, nicht nur Leads.' },
      ],
    },
    finalCta: {
      badge: 'Jetzt starten',
      headline: 'Finden Sie heraus, welches LinkedIn-Potenzial in Ihrem Unternehmen',
      headlineHighlight: 'noch ungenutzt ist.',
      subtitle: 'In einem unverbindlichen Strategiegespräch analysieren wir gemeinsam, wie LinkedIn für Ihr Unternehmen zum planbaren Revenue-Kanal werden kann.',
      cta: 'Strategiegespräch buchen',
      disclaimer: 'Kostenfrei & unverbindlich',
    },
  },
  ch: {
    hero: {
      eyebrow: 'LinkedIn Demand Generation für B2B',
      headline: 'Planbare Pipeline für erklärungsbedürftige B2B-Angebote.',
      cta: 'Strategiegespräch buchen',
      proofText: 'Erprobt bei 250+ B2B-Unternehmen',
    },
    problem: {
      headline: 'Warum LinkedIn oft',
      headlineHighlight: 'keine planbare Pipeline erzeugt.',
      subtitle: 'LinkedIn funktioniert. Aber isolierte Massnahmen führen selten zu wiederholbaren Ergebnissen. Vier typische Gründe:',
      problems: [
        { title: 'Company Pages skalieren nicht', description: 'Unternehmensseiten erzeugen Reichweite, aber keine neue Nachfrage. Algorithmus und Format begrenzen die Wirkung.' },
        { title: 'Corporate Influencer ohne System', description: 'Führungskräfte werden sichtbar, aber ohne Struktur entstehen Impressionen, keine qualifizierten Gespräche.' },
        { title: 'LinkedIn Ads allein sind teuer', description: 'Ads erzeugen Klicks, aber ohne Outbound und Nachfassung bleibt die Conversion niedrig, der CPL hoch.' },
        { title: 'Keine saubere Übergabe an Sales', description: 'Aktivitäten laufen parallel, aber Sales erhält keine priorisierten, qualifizierten Kontakte mit Kontext.' },
      ],
      conclusion: 'LinkedIn scheitert nicht am Kanal,',
      conclusionHighlight: 'sondern an der fehlenden Architektur dahinter.',
    },
    positioning: {
      headline: 'Prometheus denkt LinkedIn',
      headlineHighlight: 'ganzheitlich',
      headlineSuffix: 'von Sichtbarkeit bis zum Termin.',
      subtitle: 'Wir arbeiten mit Unternehmen, die bereits auf LinkedIn aktiv sind und merken, dass dort noch deutlich mehr möglich ist.',
      connectsLabel: 'Prometheus verbindet:',
      pillars: ['LinkedIn Ads', 'LinkedIn Outbound', 'Corporate Influencer', 'Tracking & Attribution', 'Qualifizierung & Terminierung'],
      conclusionPrefix: 'zu einem durchgängigen',
      conclusionHighlight: 'B2B-Revenue-Setup.',
    },
    services: {
      headline: 'Unsere',
      headlineHighlight: 'Leistungen',
      subtitle: 'Ein durchgängiger Funnel für planbare B2B-Gespräche über LinkedIn.',
      topRow: [
        { title: 'LinkedIn Ads & Demand Generation', description: 'Kampagnen für erklärungsbedürftige B2B-Angebote' },
        { title: 'LinkedIn Outbound & Social Selling', description: 'Strukturierte Outbound-Strategien auf Entscheider-Ebene' },
        { title: 'Corporate Influencer & Sichtbarkeit', description: 'Aufbau und Skalierung von Personenmarken' },
      ],
      middleRow: [
        { title: 'Tracking, Daten & Attribution', description: 'Sauberes Tracking aller LinkedIn-Aktivitäten auf Lead-, Kontakt- und Account-Ebene' },
        { title: 'Pipeline- & Sales-Unterstützung', description: 'Aktives Nachfassen von Leads & Opportunities für mehr Abschlüsse' },
      ],
      bottomBlock: { title: 'Qualifizierte Termine', description: 'LinkedIn erzeugt Interesse. Prometheus verwandelt es in planbare B2B-Gespräche.' },
    },
    faq: {
      headline: 'Häufig gestellte',
      headlineHighlight: 'Fragen',
      questions: [
        { question: 'Für welche Unternehmen ist Prometheus geeignet?', answer: 'Prometheus arbeitet ausschliesslich mit B2B-Unternehmen, die erklärungsbedürftige Angebote mit längeren Sales-Zyklen verkaufen. Typische Kunden sind IT-Dienstleister, Beratungen, SaaS-Unternehmen und spezialisierte B2B-Anbieter in der Schweiz und im DACH-Raum.' },
        { question: 'Wie funktioniert die Termin-Garantie?', answer: 'Wir vereinbaren gemeinsam die Zielgruppe und die Anzahl der gewünschten Termine. Unser Telemarketing-Team qualifiziert Leads und vereinbart Gespräche. Wir liefern so lange, bis die vereinbarte Anzahl an stattgefundenen Terminen erreicht ist.' },
        { question: 'Brauchen wir bereits einen LinkedIn-Auftritt?', answer: 'Nein, nicht zwingend. Wir arbeiten sowohl mit Unternehmen, die bereits auf LinkedIn aktiv sind, als auch mit solchen, die ganz am Anfang stehen. Wir analysieren gemeinsam, wo der grösste Hebel liegt.' },
        { question: 'Wie lange dauert es, bis erste Ergebnisse sichtbar werden?', answer: 'Die ersten qualifizierten Termine entstehen typischerweise innerhalb von 4 bis 8 Wochen nach Projektstart. Die genaue Timeline hängt von der Komplexität des Angebots und der Zielgruppe ab.' },
        { question: 'Wie funktioniert die Integration mit unserem CRM?', answer: 'Wir integrieren alle Aktivitäten nahtlos in gängige CRM-Systeme wie HubSpot, Salesforce oder Microsoft Dynamics. So haben Sie volle Transparenz über alle Touchpoints und können die Pipeline-Wirkung direkt nachverfolgen.' },
        { question: 'Was unterscheidet Prometheus von anderen LinkedIn-Agenturen?', answer: 'Wir denken LinkedIn ganzheitlich: von der ersten Sichtbarkeit bis zum qualifizierten Termin. Im Gegensatz zu reinen Ads- oder Outbound-Agenturen verbinden wir alle Disziplinen und garantieren echte Gespräche, nicht nur Leads.' },
      ],
    },
    finalCta: {
      badge: 'Jetzt starten',
      headline: 'Finden Sie heraus, welches LinkedIn-Potenzial bei Ihnen',
      headlineHighlight: 'noch ungenutzt ist.',
      subtitle: 'In einem unverbindlichen Strategiegespräch analysieren wir gemeinsam, wie LinkedIn für Ihr Unternehmen zum planbaren Revenue-Kanal werden kann.',
      cta: 'Strategiegespräch buchen',
      disclaimer: 'Kostenfrei & unverbindlich',
    },
  },
  en: {
    hero: {
      eyebrow: 'LinkedIn Demand Generation for B2B',
      headline: 'Predictable pipeline for complex B2B offers.',
      cta: 'Book a Strategy Call',
      proofText: 'Trusted by 250+ B2B companies',
    },
    problem: {
      headline: 'Why LinkedIn often fails to',
      headlineHighlight: 'generate a predictable pipeline.',
      subtitle: 'LinkedIn works. But isolated activities rarely lead to repeatable outcomes. Four common reasons:',
      problems: [
        { title: 'Company pages don\'t scale', description: 'Your corporate page generates reach, but not new demand. Algorithm and format limit its impact.' },
        { title: 'Corporate Influencers without structure', description: 'Your executives become visible, but without a system, impressions don\'t translate into qualified conversations.' },
        { title: 'LinkedIn Ads alone are expensive', description: 'Ads generate clicks, but without outbound and follow-up, your conversion stays low and CPL stays high.' },
        { title: 'No clean handoff to Sales', description: 'Activities run in parallel, but your Sales team doesn\'t receive prioritized, qualified contacts with context.' },
      ],
      conclusion: 'LinkedIn doesn\'t fail because of the channel,',
      conclusionHighlight: 'it fails because of missing architecture.',
    },
    positioning: {
      headline: 'Prometheus takes a',
      headlineHighlight: 'full-funnel approach to LinkedIn.',
      subtitle: 'You\'re already active on LinkedIn and sense there\'s significant untapped potential. That\'s exactly where we come in.',
      connectsLabel: 'Prometheus connects:',
      pillars: ['LinkedIn Ads', 'LinkedIn Outbound', 'Corporate Influencers', 'Tracking & Attribution', 'Qualification & Meeting Scheduling'],
      conclusionPrefix: 'into a cohesive',
      conclusionHighlight: 'B2B revenue system.',
    },
    services: {
      headline: 'Our',
      headlineHighlight: 'Services',
      subtitle: 'An integrated funnel that turns LinkedIn activity into qualified B2B meetings.',
      topRow: [
        { title: 'LinkedIn Ads & Demand Generation', description: 'Campaigns designed for your complex, high-value B2B offerings' },
        { title: 'LinkedIn Outbound & Social Selling', description: 'Structured outreach strategies targeting your ideal decision-makers' },
        { title: 'Corporate Influencers & Visibility', description: 'Developing and scaling your executive thought leadership' },
      ],
      middleRow: [
        { title: 'Tracking, Data & Attribution', description: 'End-to-end tracking of all LinkedIn activities at lead, contact, and account level' },
        { title: 'Pipeline & Sales Support', description: 'Proactive follow-up on your leads and opportunities to accelerate deal velocity' },
      ],
      bottomBlock: { title: 'Qualified Meetings', description: 'LinkedIn generates interest. Prometheus converts it into scheduled meetings your team can rely on.' },
    },
    faq: {
      headline: 'Frequently Asked',
      headlineHighlight: 'Questions',
      questions: [
        { question: 'Which companies is Prometheus designed for?', answer: 'Prometheus works exclusively with B2B companies selling complex offerings with longer sales cycles. Our clients include IT service providers, management consultancies, SaaS companies, and specialized B2B providers across the DACH region and Europe.' },
        { question: 'How does the meeting guarantee work?', answer: 'You define the target audience and desired number of meetings together with us. Our phone-based qualification team vets leads and schedules conversations. We continue until you\'ve received the agreed number of completed meetings.' },
        { question: 'Do we need an existing LinkedIn presence?', answer: 'Not necessarily. Prometheus works with companies that are already active on LinkedIn as well as those just getting started. Together, we assess where the greatest leverage lies.' },
        { question: 'How long until we see initial results?', answer: 'The first qualified meetings typically occur within 4 to 8 weeks of project kickoff. The exact timeline depends on the complexity of your offering and target audience.' },
        { question: 'How does CRM integration work?', answer: 'All activities are integrated seamlessly with leading CRM platforms such as HubSpot, Salesforce, and Microsoft Dynamics. This gives you complete visibility into every touchpoint and lets you track revenue impact directly.' },
        { question: 'What sets Prometheus apart from other LinkedIn agencies?', answer: 'Prometheus approaches LinkedIn holistically: from initial visibility to the qualified meeting. Unlike agencies focused solely on ads or outbound, we connect all disciplines and guarantee real conversations, not just leads.' },
      ],
    },
    finalCta: {
      badge: 'Get Started',
      headline: 'Discover what LinkedIn potential remains',
      headlineHighlight: 'untapped in your business.',
      subtitle: 'In a complimentary strategy session, we\'ll analyze how LinkedIn can become a predictable revenue channel for your organization.',
      cta: 'Book a Strategy Call',
      disclaimer: 'Free & no obligation',
    },
  },
};
